import { Breadcrumb } from "@chakra-ui/react";
import { useLocation, Link } from "react-router-dom";
import { t } from "@/lib/i18n";
import { Fragment, useMemo } from "react";

type Crumb = {
    /** i18n key or raw text label */
    label: string;
    /** URL to navigate to; undefined for the current/last crumb */
    path?: string;
};

/**
 * Maps the current URL pathname to an array of breadcrumb segments.
 * Falls back to an empty array (no breadcrumbs) for unknown routes.
 */
function getCrumbs(pathname: string): Crumb[] {
    const path = pathname.replace(/\/$/, "") || "/";
    const segments = path.split("/").filter(Boolean);

    // Root → redirects to clients
    if (path === "/") {
        return [{ label: "nav.clients" }];
    }

    // Static list / report pages
    switch (path) {
        case "/clients":
            return [{ label: "nav.clients" }];
        case "/payments":
            return [{ label: "nav.payments" }];
        case "/teachers":
            return [{ label: "nav.teachers" }];
        case "/students":
            return [{ label: "nav.students" }];
        case "/subjects":
            return [{ label: "nav.subjects" }];
        case "/reports/most-indebted":
            return [{ label: "nav.debt_report" }];
        case "/reports/teacher-lessons":
            return [{ label: "nav.teacher_lessons_report" }];
        case "/schedule":
            return [{ label: "nav.schedule" }];
        case "/lessons":
            return [{ label: "nav.lessons" }];
        case "/price-matrix":
            return [{ label: "nav.price_matrix" }];
        case "/me":
            return [{ label: "profile.your_profile" }];
        case "/unauthorized":
            return [{ label: "label.needs_auth" }];
    }

    // Detail pages – show parent list as a link + the entity identifier
    if (segments[0] === "client" && segments[1]) {
        return [
            { label: "nav.clients", path: "/clients" },
            { label: `${t("label.client")} #${segments[1]}` },
        ];
    }
    if (segments[0] === "payment" && segments[1]) {
        return [
            { label: "nav.payments", path: "/payments" },
            { label: `${t("label.payment")} #${segments[1]}` },
        ];
    }

    // Unrecognised route → no breadcrumbs
    return [];
}

export function Breadcrumbs() {
    const location = useLocation();

    const crumbs = useMemo(() => getCrumbs(location.pathname), [location.pathname]);

    if (crumbs.length === 0) return null;

    return (
        <Breadcrumb.Root variant="underline" size="sm">
            <Breadcrumb.List>
                {crumbs.map((crumb, i) => {
                    const isLast = i === crumbs.length - 1;
                    // Resolve i18n keys that start with a known prefix, otherwise use raw text
                    const label =
                        crumb.label.startsWith("nav.") ||
                            crumb.label.startsWith("profile.") ||
                            crumb.label.startsWith("error.") ||
                            crumb.label.startsWith("label.")
                            ? t(crumb.label)
                            : crumb.label;

                    return (
                        <Fragment key={i}>
                            <Breadcrumb.Item>
                                {isLast ? (
                                    <Breadcrumb.CurrentLink>{label}</Breadcrumb.CurrentLink>
                                ) : (
                                    <Breadcrumb.Link asChild>
                                        <Link to={crumb.path!}>{label}</Link>
                                    </Breadcrumb.Link>
                                )}
                            </Breadcrumb.Item>
                            {!isLast && <Breadcrumb.Separator />}
                        </Fragment>
                    );
                })}
            </Breadcrumb.List>
        </Breadcrumb.Root>
    );
}
